import React, { useEffect, useState, useCallback } from 'react';
import { SectionHeader, Callout } from '../components/UI';
import { api, formatApiErrorDetail } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { CheckCircle2, Clock, AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const LOGOS = {
  quickbooks: 'QuickBooks Online',
  gusto: 'Gusto',
  team_sheet: 'Google Sheet · Team Roster',
  sales_sheet: 'Google Sheet · Sales Snapshot',
  google_drive: 'Google Sheet · BHAG',
  profit_first: 'Google Sheet · Profit First (Relay)',
  vendor_master: 'Google Sheet · Vendor Master (Expense Audit)',
  expense_detail: 'Google Sheet · QBO Expense Detail (Expense Audit)',
  pnl_buckets: 'Google Sheet · Monthly P&L by Cost Center',
  slack: 'Slack · #dailyhighlight',
};

const SHEET_LINK = (id) => (id ? `https://docs.google.com/spreadsheets/d/${id}/edit` : null);

const STATUS_BADGES = {
  connected: { Icon: CheckCircle2, color: 'text-indie-evergreen', label: 'connected' },
  mock: { Icon: Clock, color: 'text-indie-ink/60', label: 'mock' },
  needs_auth: { Icon: AlertTriangle, color: 'text-indie-cobalt', label: 'needs auth' },
  error: { Icon: AlertTriangle, color: 'text-red-700', label: 'error' },
};


// HubSpot private-app token entry + connectivity test. Token is stored in
// Mongo settings (not .env) so the user can rotate it themselves without
// touching the server. Displayed as a masked input on save; never rendered
// back to the client after storage.
function HubSpotCard() {
  const [status, setStatus] = useState(null);
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const refresh = () => api.get('/integrations/hubspot/status').then((r) => setStatus(r.data));
  useEffect(() => { refresh(); }, []);

  const save = async () => {
    if (token.trim().length < 20) {
      toast.error('That token looks too short.');
      return;
    }
    setBusy(true);
    try {
      await api.post('/integrations/hubspot/token', { token: token.trim() });
      toast.success('HubSpot token saved.');
      setToken('');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      const r = await api.post('/integrations/hubspot/test');
      setTestResult(r.data);
      if (r.data.ok) toast.success('HubSpot connection OK.');
      else toast.error(r.data.error || 'Connection failed.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test failed.');
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await api.delete('/integrations/hubspot/token');
      toast.success('Token cleared.');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Clear failed.');
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;

  return (
    <section className="border-2 border-indie-ink/15 bg-white p-6 space-y-4" data-testid="integrations-hubspot-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-indie-ink/60 font-semibold">HubSpot CRM</div>
          <div className="mt-1 text-lg font-semibold text-indie-ink">Private-app token</div>
          <div className="text-xs text-indie-ink/60 mt-1">
            Feeds Sales Console (M3): contact search, deal-type mix, lead score, Protection Plans tickets pipeline.
          </div>
        </div>
        <span
          data-testid="integrations-hubspot-status"
          className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-semibold px-2 py-1 ${status.configured ? 'bg-emerald-600 text-white' : 'bg-amber-500 text-indie-ink'}`}
        >
          {status.configured ? 'Configured' : 'Not configured'}
        </span>
      </div>

      {status.saved_at && (
        <div className="text-[11px] text-indie-ink/60">
          Saved {new Date(status.saved_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
          {status.saved_by ? ` by ${status.saved_by}` : ''} · source: {status.source}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <input
          type="password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder={status.configured ? 'Paste a new token to rotate' : 'Paste your HubSpot private-app token'}
          data-testid="integrations-hubspot-token-input"
          className="flex-1 min-w-[280px] border border-indie-ink/20 px-3 py-2 text-sm font-mono"
          autoComplete="off"
        />
        <button
          data-testid="integrations-hubspot-save"
          onClick={save}
          disabled={busy || token.trim().length < 20}
          className="text-[10px] uppercase tracking-[0.14em] font-semibold bg-indie-navy text-indie-cream px-3 py-2 disabled:opacity-50"
        >
          Save
        </button>
        {status.configured && (
          <>
            <button
              data-testid="integrations-hubspot-test"
              onClick={test}
              disabled={busy}
              className="text-[10px] uppercase tracking-[0.14em] font-semibold border border-indie-ink px-3 py-2 disabled:opacity-50"
            >
              Test connection
            </button>
            <button
              data-testid="integrations-hubspot-clear"
              onClick={clear}
              disabled={busy}
              className="text-[10px] uppercase tracking-[0.14em] font-semibold border border-rose-500 text-rose-600 px-3 py-2 disabled:opacity-50"
            >
              Clear
            </button>
          </>
        )}
      </div>

      {testResult && (
        <div
          data-testid="integrations-hubspot-test-result"
          className={`text-xs px-3 py-2 ${testResult.ok ? 'bg-emerald-50 text-emerald-800 border-l-4 border-emerald-600' : 'bg-rose-50 text-rose-800 border-l-4 border-rose-600'}`}
        >
          {testResult.ok
            ? `HubSpot OK — portal ${testResult.portal_id}, ${testResult.account_type || 'account type unknown'}, tz ${testResult.time_zone || '—'}.`
            : `HubSpot failed — ${testResult.error}`}
        </div>
      )}

      <div className="text-[11px] text-indie-ink/50 pt-2 border-t border-indie-ink/10">
        Required scopes: <code>crm.objects.contacts.read</code>, <code>crm.objects.deals.read</code>, <code>tickets</code>. Optionally <code>crm.objects.users.read</code> for closer name resolution. Token is stored in the app&apos;s database, not in the codebase — you can rotate it any time.
      </div>
    </section>
  );
}


export default function Integrations() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get('/integrations/status');
    setStatus(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const triggerSlackSync = async () => {
    setSyncing('slack');
    try {
      const { data } = await api.post('/integrations/slack/sync');
      if (data.ok) {
        toast.success(`Slack sync complete — ${data.count} posts pulled.`);
      } else {
        toast.error(`Slack sync: ${data.error || 'failed'}`);
      }
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSyncing(false);
    }
  };

  const triggerSheetsSync = async () => {
    setSyncing('sheets');
    try {
      const { data } = await api.post('/integrations/sheets/sync');
      if (data.ok) {
        toast.success(`BHAG sheet sync complete — ${data.rows} rows parsed.`);
      } else {
        toast.error(`BHAG sync: ${data.error || 'failed'}`);
      }
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSyncing(false);
    }
  };

  const triggerTeamSync = async () => {
    setSyncing('team_sheet');
    try {
      const { data } = await api.post('/integrations/team/sync');
      if (data.ok) {
        toast.success(`Team roster synced — ${data.count} active teammates.`);
      } else {
        toast.error(`Team sync: ${data.error || 'failed'}`);
      }
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSyncing(false);
    }
  };

  const connectGusto = async () => {
    try {
      const { data } = await api.get('/integrations/gusto/authorize');
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const triggerGustoSync = async () => {
    setSyncing('gusto');
    try {
      const { data } = await api.post('/integrations/gusto/sync');
      if (data.ok) {
        toast.success(`Gusto roster synced — ${data.count} active teammates.`);
      } else {
        toast.error(`Gusto sync: ${data.error || 'failed'}`);
      }
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSyncing(false);
    }
  };

  const disconnectGusto = async () => {
    if (!window.confirm('Disconnect Gusto? The team roster will revert to the seeded mock until you reconnect.')) return;
    try {
      await api.post('/integrations/gusto/disconnect');
      toast.success('Gusto disconnected.');
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  // Mount-only URL param handler
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('gusto') === 'connected') {
      toast.success('Gusto connected — roster is syncing.');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('gusto') === 'error') {
      toast.error('Gusto authorization failed. Please try again.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const triggerExpenseAuditSync = async () => {
    setSyncing('expense_audit');
    try {
      const { data } = await api.post('/integrations/expense-audit/sync');
      const v = data.vendor_master;
      const e = data.expense_detail;
      if (v?.ok || e?.ok) {
        toast.success(`Synced. ${v?.ok ? `${v.count} vendors` : 'vendor sheet skipped'} · ${e?.ok ? `${e.count} txns` : 'expense sheet skipped'}`);
      } else {
        toast.error('Sync failed — check sheet IDs in env.');
      }
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSyncing(false);
    }
  };

  if (!status) return <div className="label-editorial">Loading…</div>;

  return (
    <div className="space-y-10">
      <SectionHeader
        eyebrow="Integrations"
        title="Where the numbers come from"
        description="Live sync status for every upstream source. Flip each one on by adding the provider's credentials to the server's environment."
      />

      <HubSpotCard />

      {status.google_drive?.status === 'mock' && (
        <Callout title="Google Drive — BHAG sheet ready to connect" tone="default">
          <p className="mb-3">
            Create a Google Cloud service account, enable the <span className="font-semibold">Sheets API</span>,
            download its JSON key, and paste the entire JSON as <code className="px-1 bg-white text-indie-cobalt">GOOGLE_SERVICE_ACCOUNT_JSON</code>.
            Then set <code className="px-1 bg-white text-indie-cobalt">BHAG_SPREADSHEET_ID</code> to the ID from the sheet URL
            and <span className="font-semibold">share the sheet</span> with the service-account email (Viewer access is enough).
          </p>
          <p className="text-xs text-indie-ink/60">
            Syncs daily by default. Admins can trigger an immediate refresh once configured.
          </p>
          {status.google_drive.service_account_email && (
            <p className="mt-3 text-xs text-indie-cobalt num-tabular">
              Service account detected: <span className="font-semibold">{status.google_drive.service_account_email}</span> — share the sheet with that email.
            </p>
          )}
        </Callout>
      )}

      {status.gusto?.status === 'mock' && (
        <Callout title="Gusto — team roster ready to connect" tone="default">
          <p className="mb-3">
            Create a Gusto developer app at <a href="https://dev.gusto.com" target="_blank" rel="noreferrer" className="underline text-indie-cobalt">dev.gusto.com</a>,
            register the callback URL below, then drop the Client ID and Secret into
            <code className="px-1 bg-white text-indie-cobalt"> GUSTO_CLIENT_ID</code>,
            <code className="px-1 bg-white text-indie-cobalt"> GUSTO_CLIENT_SECRET</code>, and
            <code className="px-1 bg-white text-indie-cobalt"> GUSTO_REDIRECT_URI</code>.
            After you restart the backend, click <span className="font-semibold">Connect Gusto</span> to authorize.
          </p>
          <p className="text-xs text-indie-ink/60 break-all">
            Register this callback URL in Gusto:
            <code className="ml-1 px-1 bg-white text-indie-cobalt">{`${window.location.origin}/api/integrations/gusto/callback`}</code>
          </p>
        </Callout>
      )}

      {/* Gusto onboarding callout intentionally hidden — we use the Team Roster sheet.
          The Gusto card below remains so the integration can be re-enabled later without code changes. */}

      {status.slack?.status === 'mock' && (
        <Callout title="Slack — ready to connect" tone="success">
          <p className="mb-3">
            To replace the seeded Daily Pulse with live <span className="font-semibold">#dailyhighlight</span> posts, create a Slack app,
            install it to the Indie Law workspace with <code className="px-1 bg-white text-indie-cobalt">groups:history</code>,
            <code className="px-1 bg-white text-indie-cobalt">groups:read</code>, and <code className="px-1 bg-white text-indie-cobalt">users:read</code> scopes,
            invite the bot to the channel, then paste the bot token as <code className="px-1 bg-white text-indie-cobalt">SLACK_BOT_TOKEN</code>.
          </p>
          <p className="text-xs text-indie-ink/60">
            The backend polls hourly automatically once the token is present. Admins can also trigger a sync on demand below.
          </p>
        </Callout>
      )}

      {status.slack?.status === 'error' && (
        <Callout title="Slack — something went wrong" tone="alert">
          <p>{status.slack.message}</p>
        </Callout>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {Object.entries(status).map(([key, info]) => {
          const badge = STATUS_BADGES[info.status] || STATUS_BADGES.mock;
          const Icon = badge.Icon;
          return (
            <div key={key} className="bg-white border-t-4 border-indie-evergreen p-6" data-testid={`integration-${key}`}>
              <div className="flex items-center justify-between">
                <div className="label-editorial">{LOGOS[key] || key}</div>
                <div className={`inline-flex items-center gap-1 text-xs uppercase tracking-wider font-semibold ${badge.color}`}>
                  <Icon size={14} />
                  {badge.label}
                </div>
              </div>
              <div className="mt-3 text-sm text-indie-ink/80 leading-relaxed">{info.message}</div>
              {info.spreadsheet_id && (
                <div className="mt-2">
                  <a
                    href={SHEET_LINK(info.spreadsheet_id)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`integration-${key}-sheet-link`}
                    className="inline-flex items-center gap-1 text-xs text-indie-cobalt hover:text-indie-navy font-semibold uppercase tracking-wider"
                  >
                    Open sheet ↗
                  </a>
                </div>
              )}
              <div className="mt-4 flex items-center justify-between">
                <div className="text-xs text-indie-ink/50 num-tabular">
                  Last sync: {info.last_sync ? new Date(info.last_sync).toLocaleString() : '—'}
                </div>
                {key === 'slack' && user?.is_admin && (
                  <button
                    onClick={triggerSlackSync}
                    disabled={!!syncing}
                    data-testid="slack-sync-btn"
                    className="flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold text-indie-cobalt hover:text-indie-navy disabled:opacity-50"
                  >
                    <RefreshCw size={13} className={syncing === 'slack' ? 'animate-spin' : ''} />
                    {syncing === 'slack' ? 'Syncing…' : 'Sync now'}
                  </button>
                )}
                {key === 'google_drive' && user?.is_admin && (
                  <button
                    onClick={triggerSheetsSync}
                    disabled={!!syncing}
                    data-testid="sheets-sync-btn"
                    className="flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold text-indie-cobalt hover:text-indie-navy disabled:opacity-50"
                  >
                    <RefreshCw size={13} className={syncing === 'sheets' ? 'animate-spin' : ''} />
                    {syncing === 'sheets' ? 'Syncing…' : 'Sync now'}
                  </button>
                )}
                {key === 'team_sheet' && user?.is_admin && info.status === 'connected' && (
                  <button
                    onClick={triggerTeamSync}
                    disabled={!!syncing}
                    data-testid="team-sheet-sync-btn"
                    className="flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold text-indie-cobalt hover:text-indie-navy disabled:opacity-50"
                  >
                    <RefreshCw size={13} className={syncing === 'team_sheet' ? 'animate-spin' : ''} />
                    {syncing === 'team_sheet' ? 'Syncing…' : 'Sync now'}
                  </button>
                )}
                {(key === 'vendor_master' || key === 'expense_detail' || key === 'pnl_buckets') && user?.is_admin && info.status !== 'mock' && (
                  <button
                    onClick={triggerExpenseAuditSync}
                    disabled={!!syncing}
                    data-testid={`${key}-sync-btn`}
                    className="flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold text-indie-cobalt hover:text-indie-navy disabled:opacity-50"
                  >
                    <RefreshCw size={13} className={syncing === 'expense_audit' ? 'animate-spin' : ''} />
                    {syncing === 'expense_audit' ? 'Syncing…' : 'Sync now'}
                  </button>
                )}
                {key === 'gusto' && user?.is_admin && info.status === 'needs_auth' && (
                  <button
                    onClick={connectGusto}
                    data-testid="gusto-card-connect-btn"
                    className="flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold text-indie-evergreen hover:text-indie-navy"
                  >
                    Connect
                  </button>
                )}
                {key === 'gusto' && user?.is_admin && info.status === 'connected' && (
                  <div className="flex items-center gap-3">
                    <button
                      onClick={triggerGustoSync}
                      disabled={!!syncing}
                      data-testid="gusto-sync-btn"
                      className="flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold text-indie-cobalt hover:text-indie-navy disabled:opacity-50"
                    >
                      <RefreshCw size={13} className={syncing === 'gusto' ? 'animate-spin' : ''} />
                      {syncing === 'gusto' ? 'Syncing…' : 'Sync now'}
                    </button>
                    <button
                      onClick={disconnectGusto}
                      data-testid="gusto-disconnect-btn"
                      className="text-xs uppercase tracking-wider font-semibold text-indie-neg-dark hover:text-red-700"
                    >
                      Disconnect
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="text-xs text-indie-ink/50 pt-4 border-t border-indie-navy/10">
        Want to wire up QuickBooks Online next? Drop its credentials into env and it'll switch from mock to connected the same way Slack, Google Drive, and Gusto do.
      </div>
    </div>
  );
}
